import React from 'react';
import './App.css';

import { Navigate, Route, Routes, unstable_HistoryRouter as HistoryRouter } from "react-router-dom";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { esES } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import isBetween from 'dayjs/plugin/isBetween';

import { GoogleOAuthProvider } from '@react-oauth/google';
import theme from './theme';
import { businessHistory } from './utils/businessHistory';
import ProtectedRoute from './utils/ProtectedRoute';
import { AuthProvider } from "./context/AuthContext";
import AppShell from './components/layout/AppShell';
import Landing from './components/landing/Landing';
import Register from './components/Register';
import AcceptInvite from './components/AcceptInvite';
import Home from './components/Home';
import Products from './components/Products';
import BaseCatalog from './components/BaseCatalog';
import AdminOnly from './components/AdminOnly';
import PendingProducts from './components/PendingProducts';
import ProductDetail from './components/ProductDetail';
import AddProduct from './components/AddProduct';
import BrandSettings from './components/BrandSettings';
import Categories from './components/Categories';
import ModifierGroups from './components/ModifierGroups';
import Clients from './components/Clients';
import ClientDetail from './components/ClientDetail';
import Login from './components/Login';
import Analysis from './components/Analysis';
import CreateOrganization from './components/CreateOrganization';
import Inventory from './components/Inventory';
import Promos from './components/Promos';
import Loyalty from './components/Loyalty';
import Balance from './components/Balance';
import Orders from './components/Orders';
import OrganizationWidget from './components/OrganizationWidget';
import LocationDetail from './components/LocationDetail';
import MemberDetail from './components/MemberDetail';
import Activity from './components/Activity';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Verify from './components/Verify';
import VerificationSuccess from './components/VerificationSuccess';
import Validating from './components/Validating';
import DailyReport from './components/DailyReport';
import RequestPasswordReset from './components/RequestPasswordReset';
import PasswordReset from './components/PasswordReset';
import BusinessPage from './components/business/BusinessPage';
import LoyaltyCard from './components/business/LoyaltyCard';
import InvitePage from './components/business/InvitePage';
import OrderPage from './components/business/OrderPage';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.locale('es');

// Only mount GoogleOAuthProvider when a client ID is actually configured.
// Without this guard the Google GSI script throws on every page load.
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const MaybeGoogleProvider = ({ children }: { children: React.ReactNode }) =>
  googleClientId
    ? <GoogleOAuthProvider clientId={googleClientId}>{children}</GoogleOAuthProvider>
    : <>{children}</>;

// The dashboard's pages, the same at /admin and /<slug>/admin.
const adminPages = (
  <>
      <Route index element={<Home />} />
      <Route element={<DailyReport />} path="daily-report/:reportDay" />
      <Route element={<Analysis />} path="analysis" />
      <Route element={<Clients />} path="clients" />
      <Route element={<ClientDetail />} path="clients/:id" />
      <Route element={<AdminOnly><Products /></AdminOnly>} path="products" />
      <Route element={<AdminOnly><PendingProducts /></AdminOnly>} path="products/pending" />
      <Route element={<AdminOnly><ProductDetail /></AdminOnly>} path="products/:id" />
      <Route element={<AdminOnly><AddProduct /></AdminOnly>} path="add-product" />
      <Route element={<AdminOnly><BaseCatalog /></AdminOnly>} path="common-products" />
      <Route element={<AdminOnly><Categories /></AdminOnly>} path="categories" />
      <Route element={<AdminOnly><ModifierGroups /></AdminOnly>} path="modifiers" />
      <Route element={<Navigate to="/admin/products" replace />} path="catalog" />
      <Route element={<AdminOnly><Inventory /></AdminOnly>} path="inventory" />
      <Route element={<AdminOnly><Promos /></AdminOnly>} path="promotions" />
      <Route element={<AdminOnly><Promos /></AdminOnly>} path="campaigns" />
      <Route element={<AdminOnly><Loyalty /></AdminOnly>} path="loyalty" />
      <Route element={<AdminOnly><Balance /></AdminOnly>} path="balance" />
      <Route element={<Orders />} path="orders" />
      <Route element={<AdminOnly><OrganizationWidget /></AdminOnly>} path="organization" />
      <Route element={<AdminOnly><BrandSettings /></AdminOnly>} path="brand" />
      <Route element={<AdminOnly><LocationDetail /></AdminOnly>} path="locations/:id" />
      <Route element={<AdminOnly><MemberDetail /></AdminOnly>} path="members/:id" />
      <Route element={<AdminOnly><Activity /></AdminOnly>} path="activity" />
      <Route element={<Profile />} path="profile" />
      <Route element={<Settings />} path="settings" />
  </>
);

const AppRoutes = () => {
  return (
  <Routes>
    {/* Public site: landing (also /pos, the product page), sign-up, sign-in and account emails. */}
    <Route element={<Landing />} path="/" />
    <Route element={<Landing />} path="/pos" />
    <Route element={<Login />} path="/login" />
    <Route element={<Register />} path="/register" />
    <Route element={<ProtectedRoute><CreateOrganization /></ProtectedRoute>} path="/create-organization" />
    <Route element={<AcceptInvite />} path="/accept-invite/:code" />
    <Route element={<Verify />} path="/verify" />
    <Route element={<VerificationSuccess />} path="/verification-success" />
    <Route element={<Validating />} path="/verify/:uid/:token" />
    <Route element={<RequestPasswordReset />} path="/request-password-reset" />
    <Route element={<PasswordReset />} path="/password-reset/:uid/:token" />

    {/* Owner's dashboard: shared sidebar + top bar, at /<slug>/admin (pages link to /admin/…, utils/businessHistory
        adds the slug; /admin/… itself still opens and moves to the business's address). */}
    <Route path="/admin" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>{adminPages}</Route>
    <Route path="/:slug/admin" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>{adminPages}</Route>

    {/* A business's public page: receipt.com.mx/<slug> (slugs never take the paths above). */}
    <Route element={<BusinessPage />} path="/:slug" />
    {/* The address printed on QR codes and in the plan: the same menu page. */}
    <Route element={<BusinessPage />} path="/:slug/menu" />
    <Route element={<BusinessPage />} path="/:slug/catalogo" />
    {/* A customer's loyalty card: the link printed on their ticket. */}
    <Route element={<LoyaltyCard />} path="/:slug/mi/:token" />
    {/* A customer's invitation link, shared with friends from their card. */}
    <Route element={<InvitePage />} path="/:slug/invita/:code" />
    {/* An order sent from the catalog: its status, for the customer. */}
    <Route element={<OrderPage />} path="/:slug/pedido/:token" />

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
  );
};

const App = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="es"
      localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <HistoryRouter history={businessHistory as any}>
        <AuthProvider>
          <MaybeGoogleProvider>
            <AppRoutes />
          </MaybeGoogleProvider>
        </AuthProvider>
      </HistoryRouter>
    </LocalizationProvider>
  </ThemeProvider>
);

export default App;
